/**
 * useRecurringBuilder.
 *
 * The state machine for the Recurring Builder drawer. Owns:
 *   - isOpen (whether the drawer is on screen)
 *   - draft (current working form state)
 *   - initial (the state we opened with, for isDirty detection)
 *   - errors (per-field validation, gated by showErrors below)
 *
 * showErrors stays false until the first Save attempt. The hook
 * still computes allErrors internally on every change for isValid,
 * but exposes an EMPTY errors object to the UI until Save is tried.
 * That way the drawer opens clean (no "required" nags on an empty
 * form the user just opened), errors appear the moment Save is
 * clicked on an invalid form, and they clear live as the user
 * fixes each field.
 *
 * Save button is always enabled (unless mid-save) so the user can
 * click to trigger validation surfacing. Submit short-circuits when
 * invalid; the component sees the new errors on the next render.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import type { RecurringTemplate } from "../recurringTypes";
import type {
  BuilderDraft,
  BuilderValidationErrors,
} from "./builderTypes";
import {
  createEmptyDraft,
  fromTemplate,
  toTemplate,
  validateDraft,
  isDirty as isDirtyFn,
} from "./builderUtils";

export interface UseRecurringBuilderOptions {
  onSave: (template: RecurringTemplate) => void;
  onDelete?: (templateId: string) => void;
  confirmDiscard?: () => Promise<boolean> | boolean;
}

export interface UseRecurringBuilderReturn {
  isOpen: boolean;
  draft: BuilderDraft;
  errors: BuilderValidationErrors;
  isDirty: boolean;
  isValid: boolean;
  isSaving: boolean;
  editingTemplate: RecurringTemplate | null;

  openForCreate: () => void;
  openForEdit: (template: RecurringTemplate) => void;
  close: () => Promise<void>;
  closeImmediate: () => void;

  updateDraft: (partial: Partial<BuilderDraft>) => void;
  setField: <K extends keyof BuilderDraft>(key: K, value: BuilderDraft[K]) => void;

  submit: () => void;
  requestDelete: () => void;
}

export function useRecurringBuilder(
  options: UseRecurringBuilderOptions,
): UseRecurringBuilderReturn {
  const { onSave, onDelete, confirmDiscard } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<BuilderDraft>(() => createEmptyDraft());
  const [editingTemplate, setEditingTemplate] =
    useState<RecurringTemplate | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const initialRef = useRef<BuilderDraft>(draft);

  const openForCreate = useCallback(() => {
    const fresh = createEmptyDraft();
    initialRef.current = fresh;
    setDraft(fresh);
    setEditingTemplate(null);
    setIsSaving(false);
    setShowErrors(false);
    setIsOpen(true);
  }, []);

  const openForEdit = useCallback((template: RecurringTemplate) => {
    const fromT = fromTemplate(template);
    initialRef.current = fromT;
    setDraft(fromT);
    setEditingTemplate(template);
    setIsSaving(false);
    setShowErrors(false);
    setIsOpen(true);
  }, []);

  const closeImmediate = useCallback(() => {
    setIsOpen(false);
    setEditingTemplate(null);
    setIsSaving(false);
    setShowErrors(false);
  }, []);

  const close = useCallback(async () => {
    const dirty = isDirtyFn(draft, initialRef.current);
    if (dirty && confirmDiscard) {
      const ok = await Promise.resolve(confirmDiscard());
      if (!ok) return;
    }
    closeImmediate();
  }, [draft, confirmDiscard, closeImmediate]);

  const updateDraft = useCallback((partial: Partial<BuilderDraft>) => {
    setDraft((d) => ({ ...d, ...partial }));
  }, []);

  const setField = useCallback(
    <K extends keyof BuilderDraft>(key: K, value: BuilderDraft[K]) => {
      setDraft((d) => ({ ...d, [key]: value }));
    },
    [],
  );

  const allErrors = useMemo<BuilderValidationErrors>(
    () => validateDraft(draft),
    [draft],
  );

  const errors = showErrors ? allErrors : {};

  const isValid = !showErrors || Object.keys(allErrors).length === 0;

  const isDirty = useMemo(
    () => isDirtyFn(draft, initialRef.current),
    [draft],
  );

  const submit = useCallback(() => {
    setShowErrors(true);
    const currentErrors = validateDraft(draft);
    if (Object.keys(currentErrors).length > 0) return;
    setIsSaving(true);
    const template = toTemplate(draft, editingTemplate);
    try {
      onSave(template);
      closeImmediate();
    } catch (err) {
      setIsSaving(false);
      throw err;
    }
  }, [draft, editingTemplate, onSave, closeImmediate]);

  const requestDelete = useCallback(() => {
    if (!editingTemplate || !onDelete) return;
    onDelete(editingTemplate.id);
    closeImmediate();
  }, [editingTemplate, onDelete, closeImmediate]);

  return {
    isOpen,
    draft,
    errors,
    isDirty,
    isValid,
    isSaving,
    editingTemplate,
    openForCreate,
    openForEdit,
    close,
    closeImmediate,
    updateDraft,
    setField,
    submit,
    requestDelete,
  };
}